import { Box, Button, Flex, HStack, IconButton, Switch, Tag, Text, useDisclosure } from "@chakra-ui/react";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { DataTable } from "../../../../components/DataTable";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import BespokeFactorModal from "./BespokeFactorModal";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";

const SCOPE_LABELS = {
  scope1: "Scope 1",
  scope2: "Scope 2",
  scope3_cat1: "S3 Cat 1",
  scope3_cat2: "S3 Cat 2",
  scope3_cat4: "S3 Cat 4",
  scope3_cat5: "S3 Cat 5",
  scope3_cat6: "S3 Cat 6",
  scope3_cat7: "S3 Cat 7",
  scope3_cat9: "S3 Cat 9",
  scope3_cat11: "S3 Cat 11",
  scope3_cat12: "S3 Cat 12",
  scope3_cat14: "S3 Cat 14",
  scope3_cat15: "S3 Cat 15",
};

const getDisplayName = (factor) => {
  const f = factor.fields || {};
  return f.name || f.productCategory || f.type || f.wasteType || f.modeOfTransport || "";
};

function BespokeFactorsTab({ selectedYear, organizationStructure }) {
  const [factors, setFactors] = useLocalStorage("bespokeFactors", []);
  const [overrides, setOverrides] = useLocalStorage("bespokeFactorOverrides", {});
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const getFactorValue = (factor) => {
    const yearOverrides = overrides[selectedYear];
    if (yearOverrides && yearOverrides[factor.id] !== undefined) {
      return yearOverrides[factor.id];
    }
    return factor.fields?.conversionFactor;
  };

  const handleSave = (data) => {
    if (data.id) {
      const existing = factors.find((f) => f.id === data.id);
      setFactors((prev) => prev.map((f) => f.id === data.id ? { ...f, scopeCategory: data.scopeCategory, fields: data.fields } : f));
      const existingValue = existing?.fields?.conversionFactor;
      if (data.fields.conversionFactor !== existingValue) {
        setOverrides((prev) => ({
          ...prev,
          [selectedYear]: { ...(prev[selectedYear] || {}), [data.id]: data.fields.conversionFactor },
        }));
      }
    } else {
      const id = crypto.randomUUID();
      setFactors((prev) => [...prev, { id, enabled: true, scopeCategory: data.scopeCategory, fields: data.fields }]);
    }
    setEditData(null);
  };

  const handleEdit = (factor) => {
    const currentValue = getFactorValue(factor);
    setEditData({
      ...factor,
      fields: { ...factor.fields, conversionFactor: currentValue },
    });
    onModalOpen();
  };

  const handleAdd = () => {
    setEditData(null);
    onModalOpen();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = () => {
    setFactors((prev) => prev.filter((f) => f.id !== deleteId));
    setOverrides((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((year) => {
        if (updated[year][deleteId] !== undefined) {
          const yearCopy = { ...updated[year] };
          delete yearCopy[deleteId];
          updated[year] = yearCopy;
        }
      });
      return updated;
    });
    setDeleteId(null);
    onDeleteClose();
  };

  const handleToggle = (id, enabled) => {
    setFactors((prev) => prev.map((f) => f.id === id ? { ...f, enabled } : f));
  };

  const tableData = useMemo(() => {
    return factors.map((f) => ({
      ...f,
      _displayName: getDisplayName(f),
      _displayScope: SCOPE_LABELS[f.scopeCategory] || f.scopeCategory,
      _displayUnit: f.fields?.unit || "",
      _displayFactor: getFactorValue(f),
      _displaySource: f.fields?.source || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factors, overrides, selectedYear]);

  const columns = useMemo(() => [
    {
      header: "Enabled",
      accessorKey: "enabled",
      size: 80,
      enableColumnFilter: false,
      enableSorting: false,
      skipNumericCheck: true,
      cell: ({ row }) => (
        <Switch
          size="sm"
          colorScheme="green"
          isChecked={row.original.enabled}
          onChange={(e) => handleToggle(row.original.id, e.target.checked)}
        />
      ),
    },
    {
      header: "Scope",
      accessorKey: "_displayScope",
      size: 100,
      cell: ({ getValue }) => (
        <Tag size="sm" borderRadius="full" bg="gray.100" color="gray.700" fontSize="11px">{getValue()}</Tag>
      ),
    },
    {
      header: "Name",
      accessorKey: "_displayName",
      size: 200,
    },
    {
      header: "Unit",
      accessorKey: "_displayUnit",
      size: 100,
    },
    {
      header: "Conversion Factor",
      accessorKey: "_displayFactor",
      size: 150,
      enableColumnFilter: false,
    },
    {
      header: "Source",
      accessorKey: "_displaySource",
      size: 150,
    },
    {
      header: "",
      id: "actions",
      size: 100,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <HStack spacing={1}>
          <IconButton
            aria-label="Edit"
            icon={<PencilSimple size={16} />}
            variant="ghost"
            size="sm"
            color="gray.500"
            _hover={{ color: "gray.700" }}
            onClick={() => handleEdit(row.original)}
          />
          <IconButton
            aria-label="Delete"
            icon={<Trash size={16} />}
            variant="ghost"
            size="sm"
            color="gray.500"
            _hover={{ color: "red.500" }}
            onClick={() => handleDeleteClick(row.original.id)}
          />
        </HStack>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [factors, overrides, selectedYear]);

  return (
    <Box>
      <Flex justify="flex-end" my={4}>
        <Button variant="primary" borderRadius="full" size="sm" onClick={handleAdd}>
          + Add Custom Factor
        </Button>
      </Flex>

      {tableData.length === 0 ? (
        <Text fontSize="body.m" color="gray.500" py={8} textAlign="center">
          No custom factors added yet. Click &quot;+ Add Custom Factor&quot; to create one.
        </Text>
      ) : (
        <DataTable
          data={tableData}
          columns={columns}
          enableFilters={true}
        />
      )}

      <BespokeFactorModal
        isOpen={isModalOpen}
        onClose={() => { onModalClose(); setEditData(null); }}
        onSave={handleSave}
        editData={editData}
        organizationStructure={organizationStructure}
      />

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        headerText="Delete Custom Factor"
        confirmationText="Are you sure you want to delete this custom factor? This will remove it from all years."
      />
    </Box>
  );
}

export default BespokeFactorsTab;
